// assets/dh-product-preview-init.js
// Renders DH-customized previews on the product page. Resolves color and
// product type from the URL, swaps in either a VTP-generated preview (when
// the user has a TCO hash) or the static apparel image otherwise.
(function () {
  DH.configure("visitor");

  // ---------- URL helpers ----------
  function QS() { return new URLSearchParams(location.search); }

  // /products/hat-white → { base: 'hat', color: 'white' }
  function parseHandleWithColor() {
    const parts = location.pathname.split("/").filter(Boolean);
    const idx = parts.findIndex((p) => p.toLowerCase() === "products");
    const raw = idx >= 0 && parts[idx + 1]
      ? decodeURIComponent(parts[idx + 1]).toLowerCase()
      : (parts[parts.length - 1] || "").toLowerCase();
    if (!raw) return { base: null, color: null };
    const m = raw.match(/^(hat|hoodie)(?:-([a-z0-9\-]+))?$/i);
    if (m) return { base: m[1].toLowerCase(), color: (m[2] || null)?.toLowerCase() || null };
    return { base: raw, color: null };
  }

  // ?color= wins, otherwise the suffix on apparel handles (hat-white).
  function getSelectedColor() {
    const qsColor = (QS().get("color") || "").trim().toLowerCase();
    if (qsColor) return qsColor;
    const { base, color } = parseHandleWithColor();
    if (DH.isApparelType(base) && color) return color;
    return null;
  }

  function getProductHandle() {
    return parseHandleWithColor().base;
  }

  // For hat/hoodie we always use the shared "product-apparel" template
  // (one template, many backgrounds). Anything else uses {handle}-preview.
  function resolveTemplateCode(handle) {
    const h = (handle || "").toLowerCase();
    return DH.isApparelType(h) ? "product-apparel" : `${h}-preview`;
  }

  // ---------- For apparel: make sure ?color= is in the URL on first load ----------
  (function ensureDefaultColor() {
    const m = location.pathname.match(/\/products\/([^\/?#]+)$/i);
    if (!m) return;
    const handle = m[1].toLowerCase();
    if (!(handle.endsWith("hat") || handle.endsWith("hoodie"))) return;
    const url = new URL(location.href);
    if (!url.searchParams.get("color")) {
      url.searchParams.set("color", "white");
      history.replaceState(null, "", url.toString());
    }
  })();

  // ---------- Image src swap ----------
  // Preload the target URL, then swap the actual <img>'s src and wait for
  // *its* decode before clearing the loader. The ticket dance prevents a
  // stale fetch from overwriting a newer one when refreshAll is called
  // multiple times in quick succession.
  function swapImage(img, url, onComplete) {
    const ticket = (Date.now() + Math.random()).toString(36);
    img.dataset.dhLoadTicket = ticket;

    let settled = false;
    function settle() {
      if (settled || img.dataset.dhLoadTicket !== ticket) return;
      settled = true;
      DH.setImgLoading(img, false);
      delete img.dataset.dhLoadTicket;
      onComplete?.();
    }

    const preload = new Image();
    preload.crossOrigin = "anonymous";
    preload.src = url;

    function revealWhenReady() {
      if (img.dataset.dhLoadTicket !== ticket) return;
      const finish = () => {
        if (img.dataset.dhLoadTicket !== ticket) return;
        // Keep the zoom modal's full-size image in sync (otherwise clicking
        // the hero opens the original, un-customized product image).
        syncModalImage(img, url);
        settle();
      };
      if (img.decode) {
        img.decode().then(finish).catch(() => {
          if (img.complete && img.naturalWidth > 0) finish();
          else img.addEventListener("load", finish, { once: true });
        });
      } else if (img.complete && img.naturalWidth > 0) {
        finish();
      } else {
        img.addEventListener("load", finish, { once: true });
      }
    }

    preload.onload = () => {
      if (img.dataset.dhLoadTicket !== ticket) return;

      // Kill responsive sources first so nothing fights our src.
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.setAttribute("data-srcset", "");
      const picture = img.closest("picture");
      picture?.querySelectorAll("source[srcset]").forEach((s) => {
        s.removeAttribute("srcset");
        s.setAttribute("data-srcset", "");
      });

      img.setAttribute("src", url);
      img.setAttribute("data-src", url);
      revealWhenReady();
    };

    preload.onerror = settle;

    // Safety net in case onload/onerror never fire.
    setTimeout(settle, 12000);
  }

  // Mirror a swapped preview into the fullscreen zoom modal. The gallery
  // thumbnail's modal-opener carries a button[data-media-id]; the modal
  // renders a matching <img data-media-id> with the original product URL.
  function syncModalImage(img, url) {
    const mediaId = img
      .closest("modal-opener")
      ?.querySelector("button[data-media-id]")
      ?.getAttribute("data-media-id");
    if (!mediaId) return;

    const modalImg = document.querySelector(
      `product-modal img[data-media-id="${mediaId}"]`
    );
    if (!modalImg) return;
    modalImg.removeAttribute("srcset");
    modalImg.removeAttribute("sizes");
    modalImg.setAttribute("src", url);
  }

  function apparelTypeOf(img, fallbackHandle) {
    return (
      img.getAttribute("data-product-handle") ||
      img.getAttribute("data_product_handle") ||
      fallbackHandle ||
      "sticker"
    ).toLowerCase();
  }

  // Render a single preview image. Calls onDone() when it settles (success,
  // error, or no-op) so callers can sequence the next render.
  function renderPreview(img, ctx, onDone) {
    DH.setImgLoading(img, true);

    const apparelType  = apparelTypeOf(img, ctx.handle);
    const templateCode = resolveTemplateCode(apparelType);
    const isApparel    = DH.isApparelType(apparelType);

    const vtpOpts = {
      template_code: templateCode,
      width: DH.getPreviewWidth(img),
      customizations_hash: ctx.hasHash ? ctx.hash : undefined
    };

    if (isApparel) {
      const apparel = DH.buildApparelParams(apparelType, ctx.color, ctx.hasHash);
      if (apparel) Object.assign(vtpOpts, apparel);
    }

    if (ctx.hasHash) {
      // User has a logo — render the customized preview via VTP.
      DSHDLib.getVariableTemplatePreviewURL(vtpOpts, (err, url) => {
        if (err || !url) {
          if (isApparel) swapImage(img, DH.resolveApparelBg(apparelType, ctx.color, false), onDone);
          else { DH.setImgLoading(img, false); onDone?.(); }
          return;
        }
        swapImage(img, url, onDone);
      });
    } else if (isApparel) {
      // No logo — show the static default apparel image.
      swapImage(img, DH.resolveApparelBg(apparelType, ctx.color, false), onDone);
    } else {
      // Non-apparel without a logo: nothing to swap.
      DH.setImgLoading(img, false);
      onDone?.();
    }
  }

  // ---------- Main refresh pass ----------
  // opts.onlyApparel — skip non-apparel previews (used on color change,
  //   since color only affects apparel backgrounds).
  // The hero (first preview in document order) renders first; the rest
  // render only after it finishes, so they don't starve it of bandwidth.
  function refreshAll(opts = {}) {
    const hash = DH.readTcoHash();
    const ctx = {
      hash,
      hasHash: !!hash,
      color: getSelectedColor() || "white",
      handle: getProductHandle() || "sticker"
    };

    let targets = Array.from(document.querySelectorAll(".js-dh-preview"));
    if (opts.onlyApparel) {
      targets = targets.filter((img) => DH.isApparelType(apparelTypeOf(img, ctx.handle)));
    }
    if (!targets.length) return;

    const [main, ...rest] = targets;
    renderPreview(main, ctx, () => {
      rest.forEach((img) => renderPreview(img, ctx));
    });
  }

  // ---------- Color menu (URL is the single source of truth) ----------
  function bindColorMenu() {
    const menu = document.querySelector(".dh-color-menu");
    if (!menu) return;

    // Sync radios to URL on load (case-insensitive: values are capitalized).
    const c = (getSelectedColor() || "").toLowerCase();
    if (c) {
      const match = [...menu.querySelectorAll('input[type="radio"]')]
        .find((r) => (r.value || "").toLowerCase() === c);
      if (match) match.checked = true;
    }

    menu.addEventListener("click", (evt) => {
      if (!evt.target?.matches?.("input[type='radio']")) return;
      const selected = (evt.target.value || "").toLowerCase();

      const url = new URL(location.href);
      url.searchParams.set("color", selected);
      history.replaceState(null, "", url.toString());

      // Color only affects apparel backgrounds — don't re-render the
      // non-apparel previews (stickers, etc.) on a color change.
      refreshAll({ onlyApparel: true });
    }, true);
  }

  // ---------- Boot ----------
  window.addEventListener("load", () => {
    bindColorMenu();
    refreshAll();
  });

  // Re-run when product-recommendations inject new preview imgs.
  const rec = document.querySelector(
    'product-recommendations, #product-recommendations, [data-section-type="product-recommendations"]'
  );
  if (rec) {
    const mo = new MutationObserver(() => {
      if (rec.querySelector(".js-dh-preview")) {
        refreshAll();
        mo.disconnect();
      }
    });
    mo.observe(rec, { childList: true, subtree: true });
  }
})();
